import { useCallback, useEffect, useState } from "react";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { useToast } from "providers/ToastProvider";
import {
    patchEthernetProfile,
    type EthernetSavedProfile,
} from "lib/ethernetApi";

type Props = {
    profile: EthernetSavedProfile | null;
    baseUrl: string | null;
    onClose: () => void;
    onDone?: () => void;
};

const EthernetProfileEditDialog: React.FC<Props> = ({
    profile,
    baseUrl,
    onClose,
    onDone,
}) => {
    const { addToast } = useToast();
    const [displayName, setDisplayName] = useState("");
    const [ipv4Method, setIpv4Method] = useState<"auto" | "manual">("auto");
    const [ipv4Address, setIpv4Address] = useState("");
    const [ipv4Gateway, setIpv4Gateway] = useState("");
    const [autoconnect, setAutoconnect] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const normalizeManualIpv4Address = useCallback((value: string): string => {
        const trimmed = value.trim();
        if (!trimmed) return "";
        return trimmed.includes("/") ? trimmed : `${trimmed}/24`;
    }, []);

    const visible = profile != null;

    useEffect(() => {
        if (!profile) return;
        setDisplayName(profile.display_name ?? "");
        setIpv4Method(
            (profile.ipv4_method || "").toLowerCase() === "manual" ? "manual" : "auto"
        );
        setIpv4Address(profile.ipv4_address ?? "");
        setIpv4Gateway(profile.ipv4_gateway ?? "");
        setAutoconnect(!!profile.autoconnect);
        setSubmitting(false);
    }, [profile]);

    const handleSubmit = useCallback(async () => {
        if (!baseUrl || !profile) return;
        if (ipv4Method === "manual") {
            const addr = ipv4Address.trim();
            if (!addr) {
                addToast({
                    type: "error",
                    message: "Enter an IPv4 address (e.g. 192.168.1.50).",
                });
                return;
            }
        }

        setSubmitting(true);
        try {
            await patchEthernetProfile(baseUrl, profile.id, {
                display_name: displayName.trim() || null,
                ipv4_method: ipv4Method,
                ipv4_address:
                    ipv4Method === "manual" ? normalizeManualIpv4Address(ipv4Address) : null,
                ipv4_gateway: ipv4Method === "manual" ? ipv4Gateway.trim() || null : null,
                autoconnect,
            });
            addToast({ type: "success", message: "Profile updated" });
            onDone?.();
            onClose();
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Update failed",
            });
        } finally {
            setSubmitting(false);
        }
    }, [
        addToast,
        autoconnect,
        baseUrl,
        displayName,
        ipv4Address,
        ipv4Gateway,
        ipv4Method,
        normalizeManualIpv4Address,
        onClose,
        onDone,
        profile,
    ]);

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            className="max-h-[min(90vh,40rem)] w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
            zIndexClassName="z-[10050]"
        >
            {profile ? (
                <>
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <h3 className="text-h4 font-semibold text-white">Edit profile</h3>
                        <CloseButton
                            className="text-black60 hover:text-black40"
                            onClick={submitting ? undefined : onClose}
                        />
                    </div>
                    <p className="mb-4 text-h5 text-black40">
                        Changes apply to the saved history entry. Use Connect on the profile to
                        apply to the interface.
                    </p>

                    <div className="space-y-3">
                        <div>
                            <label
                                className="text-h5 text-black40"
                                htmlFor="eth-edit-profile-name"
                            >
                                Profile name
                            </label>
                            <input
                                id="eth-edit-profile-name"
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value.slice(0, 80))}
                                maxLength={80}
                                placeholder="e.g. Lab bench / router uplink"
                                className="mt-1 w-full rounded-r5 bg-black70 px-3 py-2 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                            />
                        </div>

                        <div>
                            <p className="text-h5 text-black40">IPv4</p>
                            <div className="mt-1 flex flex-col gap-2">
                                <label className="flex cursor-pointer items-center gap-2 text-h5 text-white">
                                    <input
                                        type="radio"
                                        name="eth-profile-edit-ipv4"
                                        checked={ipv4Method === "auto"}
                                        onChange={() => setIpv4Method("auto")}
                                        className="accent-primary2"
                                    />
                                    Automatic (DHCP)
                                </label>
                                <label className="flex cursor-pointer items-center gap-2 text-h5 text-white">
                                    <input
                                        type="radio"
                                        name="eth-profile-edit-ipv4"
                                        checked={ipv4Method === "manual"}
                                        onChange={() => setIpv4Method("manual")}
                                        className="accent-primary2"
                                    />
                                    Manual (static)
                                </label>
                            </div>
                        </div>

                        {ipv4Method === "manual" ? (
                            <div className="space-y-3 border-t border-black80 pt-3">
                                <div>
                                    <label
                                        className="mb-1 block text-h5 text-black40"
                                        htmlFor="eth-edit-profile-addr"
                                    >
                                        Address
                                    </label>
                                    <input
                                        id="eth-edit-profile-addr"
                                        type="text"
                                        value={ipv4Address}
                                        onChange={(e) => setIpv4Address(e.target.value)}
                                        placeholder="192.168.1.50"
                                        className="w-full rounded-r5 bg-black70 px-3 py-2 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                                    />
                                </div>
                                <div>
                                    <label
                                        className="mb-1 block text-h5 text-black40"
                                        htmlFor="eth-edit-profile-gw"
                                    >
                                        Gateway (optional)
                                    </label>
                                    <input
                                        id="eth-edit-profile-gw"
                                        type="text"
                                        value={ipv4Gateway}
                                        onChange={(e) => setIpv4Gateway(e.target.value)}
                                        placeholder="192.168.1.1"
                                        className="w-full rounded-r5 bg-black70 px-3 py-2 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                                    />
                                </div>
                            </div>
                        ) : null}

                        <label className="flex cursor-pointer items-center gap-2 border-t border-black80 pt-3 text-h5 text-white">
                            <input
                                type="checkbox"
                                checked={autoconnect}
                                onChange={(e) => setAutoconnect(e.target.checked)}
                                className="accent-primary2"
                            />
                            Connect automatically
                        </label>
                    </div>

                    <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-black80 pt-3">
                        <FunctionalButton
                            className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                            title="Cancel"
                            titleClassName="!normal-case text-h5"
                            disabled={submitting}
                            onClick={onClose}
                        />
                        <FunctionalButton
                            className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1"
                            title={submitting ? "…" : "Save"}
                            titleClassName="!normal-case text-h5"
                            disabled={submitting || !baseUrl}
                            onClick={() => void handleSubmit()}
                        />
                    </div>
                </>
            ) : null}
        </AnimatedModal>
    );
};

export default EthernetProfileEditDialog;
